import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import QuizHeader from './components/QuizHeader';
import QuestionCard from './components/QuestionCard';
import QuizNavigation from './components/QuizNavigation';
import QuizCompletionModal from './components/QuizCompletionModal';
import QuizLoadingState from './components/QuizLoadingState';
import QuestionOverview from './components/QuestionOverview';
import { quizAPI } from 'service';


const QuizAssessmentInterface = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const courseId = params.get('chapterId') || '1';
  const { name } = location.state || {};

  // Quiz state
  const [isLoading, setIsLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(600);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [startTime] = useState(Date.now());
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);

  const [quizData, setQuizData] = useState(null);

  // Mock quiz data
  const scienceQuiz = {
    chapterId: "1",
    totalQuestions: 10,
    timeLimit: 600, // 10 minutes
    passingScore: 70,
    questions: [
      {
        id: 1,
        question: "What does AI stand for in the context of computer science?",
        context: "AI is a fundamental concept in modern technology and computing.",
        options: [
          { id: 'a', text: "Automated Intelligence" },
          { id: 'b', text: "Artificial Intelligence" },
          { id: 'c', text: "Advanced Integration" },
          { id: 'd', text: "Algorithmic Implementation" }
        ],
        correctAnswer: 'b',
        explanation: "AI stands for Artificial Intelligence, which refers to the simulation of human intelligence in machines that are programmed to think and learn like humans."
      },
      {
        id: 2,
        question: "Which of the following is considered a subset of AI?",
        options: [
          { id: 'a', text: "Machine Learning" },
          { id: 'b', text: "Database Management" },
          { id: 'c', text: "Web Development" },
          { id: 'd', text: "Network Security" }
        ],
        correctAnswer: 'a',
        explanation: "Machine Learning is a subset of AI that focuses on the development of algorithms that can learn and improve from experience without being explicitly programmed."
      },
      {
        id: 3,
        question: "What is the primary goal of supervised learning?",
        context: "Supervised learning is one of the main types of machine learning approaches.",
        options: [
          { id: 'a', text: "To find hidden patterns in data without labels" },
          { id: 'b', text: "To learn from labeled training data to make predictions" },
          { id: 'c', text: "To optimize actions through trial and error" },
          { id: 'd', text: "To reduce the dimensionality of data" }
        ],
        correctAnswer: 'b',
        explanation: "Supervised learning uses labeled training data to learn a mapping function that can make predictions on new, unseen data."
      },
      {
        id: 4,
        question: "Which of these is an example of unsupervised learning?",
        options: [
          { id: 'a', text: "Email spam detection" },
          { id: 'b', text: "Image classification" },
          { id: 'c', text: "Customer segmentation" },
          { id: 'd', text: "Speech recognition" }
        ],
        correctAnswer: 'c',
        explanation: "Customer segmentation is an example of unsupervised learning where the algorithm finds patterns and groups customers without predefined labels."
      },
      {
        id: 5,
        question: "What is a neural network inspired by?",
        options: [
          { id: 'a', text: "Computer processors" },
          { id: 'b', text: "The human brain" },
          { id: 'c', text: "Mathematical equations" },
          { id: 'd', text: "Database structures" }
        ],
        correctAnswer: 'b',
        explanation: "Neural networks are inspired by the structure and function of biological neural networks in the human brain, with interconnected nodes (neurons) that process information."
      },
      {
        id: 6,
        question: "What is the purpose of training data in machine learning?",
        context: "Training data is essential for developing effective machine learning models.",
        options: [
          { id: 'a', text: "To test the final model performance" },
          { id: 'b', text: "To teach the algorithm patterns and relationships" },
          { id: 'c', text: "To store the model parameters" },
          { id: 'd', text: "To validate the model accuracy" }
        ],
        correctAnswer: 'b',
        explanation: "Training data is used to teach the machine learning algorithm patterns and relationships so it can make accurate predictions on new data."
      },
      {
        id: 7,
        question: "Which term describes AI that can perform any intellectual task that a human can do?",
        options: [
          { id: 'a', text: "Narrow AI" },
          { id: 'b', text: "General AI" },
          { id: 'c', text: "Weak AI" },
          { id: 'd', text: "Applied AI" }
        ],
        correctAnswer: 'b',
        explanation: "General AI (or Artificial General Intelligence) refers to AI systems that can understand, learn, and apply knowledge across a wide range of tasks at a level equal to human intelligence."
      },
      {
        id: 8,
        question: "What is overfitting in machine learning?",
        options: [
          { id: 'a', text: "When a model performs well on training data but poorly on new data" },
          { id: 'b', text: "When a model is too simple to capture patterns" },
          { id: 'c', text: "When training takes too long to complete" },
          { id: 'd', text: "When the dataset is too large" }
        ],
        correctAnswer: 'a',
        explanation: "Overfitting occurs when a model learns the training data too well, including noise and irrelevant patterns, making it perform poorly on new, unseen data."
      },
      {
        id: 9,
        question: "Which of the following is a common application of Natural Language Processing (NLP)?",
        context: "NLP is a branch of AI that deals with the interaction between computers and human language.",
        options: [
          { id: 'a', text: "Image recognition" },
          { id: 'b', text: "Chatbots and virtual assistants" },
          { id: 'c', text: "Autonomous vehicles" },
          { id: 'd', text: "Recommendation systems" }
        ],
        correctAnswer: 'b',
        explanation: "Chatbots and virtual assistants are common applications of NLP, as they need to understand and generate human language to interact effectively with users."
      },
      {
        id: 10,
        question: "What is the main advantage of deep learning over traditional machine learning?",
        options: [
          { id: 'a', text: "It requires less computational power" },
          { id: 'b', text: "It can automatically learn features from raw data" },
          { id: 'c', text: "It works better with small datasets" },
          { id: 'd', text: "It is easier to interpret" }
        ],
        correctAnswer: 'b',
        explanation: "Deep learning can automatically learn hierarchical features from raw data without manual feature engineering, making it particularly effective for complex tasks like image and speech recognition."
      }
    ]
  };

  const dataScienceQuiz = {
    chapterTitle: "Introduction to Data Science",
    chapterId: "2",
    totalQuestions: 10,
    timeLimit: 600,
    passingScore: 70,
    questions: [
      {
        id: 1,
        question: "What is the primary goal of data science?",
        options: [
          { id: 'a', text: "To build hardware systems" },
          { id: 'b', text: "To interpret and extract insights from data" },
          { id: 'c', text: "To manage databases" },
          { id: 'd', text: "To create websites" }
        ],
        correctAnswer: 'b',
        explanation: "Data science is focused on analyzing data and deriving meaningful insights to support decision-making."
      },
      {
        id: 2,
        question: "Which programming language is commonly used in data science?",
        options: [
          { id: 'a', text: "HTML" },
          { id: 'b', text: "Python" },
          { id: 'c', text: "CSS" },
          { id: 'd', text: "JavaFX" }
        ],
        correctAnswer: 'b',
        explanation: "Python is widely used in data science due to its rich ecosystem of libraries like pandas, NumPy, and scikit-learn."
      },
      {
        id: 3,
        question: "What is a dataset?",
        options: [
          { id: 'a', text: "A programming error" },
          { id: 'b', text: "A collection of webpages" },
          { id: 'c', text: "A structured collection of data" },
          { id: 'd', text: "A software tool" }
        ],
        correctAnswer: 'c',
        explanation: "A dataset is an organized collection of data, typically in tabular form."
      },
      {
        id: 4,
        question: "Which of the following is a data visualization library in Python?",
        options: [
          { id: 'a', text: "React" },
          { id: 'b', text: "TensorFlow" },
          { id: 'c', text: "Matplotlib" },
          { id: 'd', text: "Flask" }
        ],
        correctAnswer: 'c',
        explanation: "Matplotlib is a popular Python library for creating static, animated, and interactive plots."
      },
      {
        id: 5,
        question: "What does 'cleaning data' mean?",
        options: [
          { id: 'a', text: "Encrypting data" },
          { id: 'b', text: "Removing errors and inconsistencies" },
          { id: 'c', text: "Formatting data to HTML" },
          { id: 'd', text: "Erasing all data" }
        ],
        correctAnswer: 'b',
        explanation: "Data cleaning involves fixing or removing incorrect, corrupted, or incomplete data."
      },
      {
        id: 6,
        question: "What is the output of a classification algorithm?",
        options: [
          { id: 'a', text: "A continuous number" },
          { id: 'b', text: "A category or label" },
          { id: 'c', text: "A web page" },
          { id: 'd', text: "A plot graph" }
        ],
        correctAnswer: 'b',
        explanation: "Classification algorithms predict categorical values, like 'spam' or 'not spam'."
      },
      {
        id: 7,
        question: "Which of the following is an example of structured data?",
        options: [
          { id: 'a', text: "Sensor logs" },
          { id: 'b', text: "Social media posts" },
          { id: 'c', text: "CSV files with columns and rows" },
          { id: 'd', text: "Audio recordings" }
        ],
        correctAnswer: 'c',
        explanation: "Structured data fits into predefined models like rows and columns of a table."
      },
      {
        id: 8,
        question: "What is 'correlation' in data analysis?",
        options: [
          { id: 'a', text: "A measure of visual clarity" },
          { id: 'b', text: "A type of neural network" },
          { id: 'c', text: "A measure of the relationship between variables" },
          { id: 'd', text: "A way to group data randomly" }
        ],
        correctAnswer: 'c',
        explanation: "Correlation measures the degree to which two variables move in relation to each other."
      },
      {
        id: 9,
        question: "Which role focuses on interpreting and communicating data insights?",
        options: [
          { id: 'a', text: "Backend developer" },
          { id: 'b', text: "Data analyst" },
          { id: 'c', text: "UX designer" },
          { id: 'd', text: "QA tester" }
        ],
        correctAnswer: 'b',
        explanation: "Data analysts interpret data and communicate insights that guide business decisions."
      },
      {
        id: 10,
        question: "What is a histogram used for?",
        options: [
          { id: 'a', text: "Showing website layouts" },
          { id: 'b', text: "Displaying the frequency distribution of data" },
          { id: 'c', text: "Encrypting passwords" },
          { id: 'd', text: "Creating 3D models" }
        ],
        correctAnswer: 'b',
        explanation: "A histogram visualizes how often each range of values occurs in a dataset."
      }
    ]
  };

  const webDevQuiz = {
    chapterTitle: "Fundamentals of Web Development",
    chapterId: "3",
    totalQuestions: 10,
    timeLimit: 600,
    passingScore: 70,
    questions: [
      {
        id: 1,
        question: "What does HTML stand for?",
        options: [
          { id: 'a', text: "HighText Machine Language" },
          { id: 'b', text: "HyperText Markup Language" },
          { id: 'c', text: "Hyper Transfer Markup Language" },
          { id: 'd', text: "Home Tool Markup Language" }
        ],
        correctAnswer: 'b',
        explanation: "HTML is the standard markup language used to create web pages."
      },
      {
        id: 2,
        question: "Which language is used for styling web pages?",
        options: [
          { id: 'a', text: "HTML" },
          { id: 'b', text: "Python" },
          { id: 'c', text: "CSS" },
          { id: 'd', text: "Java" }
        ],
        correctAnswer: 'c',
        explanation: "CSS (Cascading Style Sheets) is used to style and layout HTML elements."
      },
      {
        id: 3,
        question: "What does JavaScript primarily do in web development?",
        options: [
          { id: 'a', text: "Manages databases" },
          { id: 'b', text: "Adds interactivity to web pages" },
          { id: 'c', text: "Creates static content" },
          { id: 'd', text: "Styles web pages" }
        ],
        correctAnswer: 'b',
        explanation: "JavaScript enables interactive behaviors like clicks, forms, and animations."
      },
      {
        id: 4,
        question: "Which tag is used to create a hyperlink in HTML?",
        options: [
          { id: 'a', text: "<a>" },
          { id: 'b', text: "<link>" },
          { id: 'c', text: "<href>" },
          { id: 'd', text: "<url>" }
        ],
        correctAnswer: 'a',
        explanation: "The `<a>` tag defines a hyperlink, which is used to link from one page to another."
      },
      {
        id: 5,
        question: "What is the role of a web browser?",
        options: [
          { id: 'a', text: "To compile code" },
          { id: 'b', text: "To display web pages" },
          { id: 'c', text: "To run SQL queries" },
          { id: 'd', text: "To create APIs" }
        ],
        correctAnswer: 'b',
        explanation: "A browser interprets HTML, CSS, and JavaScript to display websites."
      },
      {
        id: 6,
        question: "Which HTML tag is used to display an image?",
        options: [
          { id: 'a', text: "<pic>" },
          { id: 'b', text: "<img>" },
          { id: 'c', text: "<src>" },
          { id: 'd', text: "<image>" }
        ],
        correctAnswer: 'b',
        explanation: "The `<img>` tag is used to embed images in web pages."
      },
      {
        id: 7,
        question: "Which of the following is a CSS framework?",
        options: [
          { id: 'a', text: "React" },
          { id: 'b', text: "Flask" },
          { id: 'c', text: "Bootstrap" },
          { id: 'd', text: "NumPy" }
        ],
        correctAnswer: 'c',
        explanation: "Bootstrap is a popular CSS framework for responsive design."
      },
      {
        id: 8,
        question: "What does the DOM represent?",
        options: [
          { id: 'a', text: "Design Object Model" },
          { id: 'b', text: "Document Object Model" },
          { id: 'c', text: "Data Object Module" },
          { id: 'd', text: "Dynamic Output Management" }
        ],
        correctAnswer: 'b',
        explanation: "The DOM represents the page structure and allows JavaScript to manipulate HTML elements."
      },
      {
        id: 9,
        question: "Which HTTP method is typically used to retrieve data?",
        options: [
          { id: 'a', text: "POST" },
          { id: 'b', text: "PUT" },
          { id: 'c', text: "GET" },
          { id: 'd', text: "DELETE" }
        ],
        correctAnswer: 'c',
        explanation: "GET requests are used to retrieve data from a server."
      },
      {
        id: 10,
        question: "Which company created React?",
        options: [
          { id: 'a', text: "Google" },
          { id: 'b', text: "Microsoft" },
          { id: 'c', text: "Facebook" },
          { id: 'd', text: "Amazon" }
        ],
        correctAnswer: 'c',
        explanation: "React was developed by Facebook to build interactive UIs."
      }
    ]
  };


  useEffect(() => {
    if (courseId === '1') {
      setQuizData(scienceQuiz);
    } else if (courseId === '2') {
      setQuizData(dataScienceQuiz);
    } else if (courseId === '3') {
      setQuizData(webDevQuiz);
    } else {
      setQuizData(scienceQuiz);
    }
  }, [courseId]);

  // Initialize quiz
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Timer countdown
  useEffect(() => {
    if (isLoading || isQuizCompleted) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isLoading, isQuizCompleted]);

  const currentQuestion = quizData?.questions[currentQuestionIndex];
  const selectedAnswer = selectedAnswers[currentQuestion?.id];

  const handleAnswerSelect = (answerId) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answerId
    }));
    setShowFeedback(true);

    // Auto-advance after showing feedback
    setTimeout(() => {
      setShowFeedback(false);
      if (currentQuestionIndex < quizData.questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      }
    }, 2000);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < quizData.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowFeedback(false);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowFeedback(false);
    }
  };

  const handleSubmitQuiz = async () => {
    const correctAnswers = quizData.questions.filter(
      question => selectedAnswers[question.id] === question.correctAnswer
    ).length;

    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    const score = Math.round((correctAnswers / quizData.questions.length) * 100);

    const payload = {
      courseRef: quizData.chapterId,
      score,
      timeSpentInMinutes: Math.floor(timeSpent / 60),
      totalQuestions: quizData.questions.length,
      correctAnswers: correctAnswers,
      wrongAnswers: quizData.questions.length - correctAnswers,
      questions: quizData.questions.map(q => {
        const answerOptions = q.options.map(opt => opt.text);
        const correctAnswerText = q.options.find(opt => opt.id === q.correctAnswer)?.text || null;
        const userAnswerText = q.options.find(opt => opt.id === selectedAnswers[q.id])?.text || null;

        return {
          question: q.question,
          answers: answerOptions,
          correctAnswer: correctAnswerText,
          userAnswer: userAnswerText
        };
      })
    };

    const response = await quizAPI.submitQuiz(payload);

    setQuizResults({
      score,
      correctAnswers,
      totalQuestions: quizData.questions.length,
      timeSpent,
      passingScore: quizData.passingScore
    });

    setIsQuizCompleted(true);
  };

  const handleRetakeQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowFeedback(false);
    setTimeRemaining(quizData.timeLimit);
    setIsQuizCompleted(false);
    setQuizResults(null);
  };

  const handleExitQuiz = () => {
    navigate('/chapter-overview-dashboard');
  };

  const canGoNext = selectedAnswer && !showFeedback;
  const canGoPrevious = currentQuestionIndex > 0 && !showFeedback;
  const isLastQuestion = currentQuestionIndex === quizData?.questions?.length - 1;

  if (isLoading) {
    return <QuizLoadingState message="Loading quiz questions..." />;
  }
  const handleQuestionSelect = (questionId) => {
    const questionIndex = quizData.questions.findIndex(q => q.id === questionId);
    if (questionIndex !== -1) {
      setCurrentQuestionIndex(questionIndex);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-16">
        <QuizHeader
          currentQuestion={currentQuestionIndex + 1}
          totalQuestions={quizData.questions.length}
          timeRemaining={timeRemaining}
          chapterTitle={quizData.chapterTitle}
        />

        {/* Main Quiz Content */}
        <div className="flex-1 px-4 py-8 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <QuestionCard
              question={currentQuestion}
              onAnswerSelect={handleAnswerSelect}
              selectedAnswer={selectedAnswer}
              showFeedback={showFeedback}
              isSubmitted={showFeedback}
            />
          </div>
        </div>

        <QuizNavigation
          currentQuestion={currentQuestionIndex + 1}
          totalQuestions={quizData.questions.length}
          selectedAnswer={selectedAnswer}
          canGoNext={canGoNext}
          canGoPrevious={canGoPrevious}
          isLastQuestion={isLastQuestion}
          onNext={handleNextQuestion}
          onPrevious={handlePreviousQuestion}
          onSubmitQuiz={handleSubmitQuiz}
          onExit={handleExitQuiz}
          showFeedback={showFeedback}
        />
      </div>

      <QuizCompletionModal
        isOpen={isQuizCompleted}
        score={quizResults?.score || 0}
        totalQuestions={quizResults?.totalQuestions || 0}
        correctAnswers={quizResults?.correctAnswers || 0}
        timeSpent={quizResults?.timeSpent || 0}
        passingScore={quizResults?.passingScore || 70}
        onRetakeQuiz={handleRetakeQuiz}
      />

      <QuestionOverview
        questions={quizData.questions}
        currentQuestion={currentQuestion.id}
        answers={answers}
        flaggedQuestions={flaggedQuestions}
        onQuestionSelect={handleQuestionSelect}
        isOpen={isOverviewOpen}
        onToggle={() => setIsOverviewOpen(!isOverviewOpen)}
      />
    </div>
  );
};

export default QuizAssessmentInterface;